import { clientMoveIn } from "../lib/clientMoveIn";
// src/services/emailService.ts
import { supabase } from "../lib/supabase";
import { fetchWithAuth } from "../lib/api";
import { runtimeEnvironment } from "../lib/runtimeEnvironment";
import { clientStore } from "../lib/clientStorage";
import {
  getPersistenceGeneration,
  hasPersistenceInvalidationFence,
  persistenceWriteAllowed,
  PERSISTENCE_LOCAL_CLEAR_KEY,
  readPersistenceEvent,
} from "../lib/persistenceIdentity";
import {
  EMAIL_CONFIG_POLICY,
  valueFitsPolicy,
} from "../lib/persistenceRegistry";

// ==================== Constants ====================

export const COMPANY_NAME = "LocateNow, LLC";
export const COMPANY_LOGO_URL = `${runtimeEnvironment.assetBaseUrl}/locatenow-logo.png`;
export const COMPANY_LOGO_WHITE_URL = `${runtimeEnvironment.assetBaseUrl}/locatenow-logo-white.png`;
export const DEFAULT_LOCATOR_PHOTO_URL = COMPANY_LOGO_URL;
export const INSTAGRAM_HANDLE = "@locatenow.atl";
export const INSTAGRAM_URL = "https://www.instagram.com/locatenow.atl/";
// White-outline Instagram glyph used in the locator card's gradient
// band. Hosted on the same S3 bucket as the company logos so email
// clients fetch it with no extra CDN dependency. Hidden on mobile via
// CSS (the band text is enough on narrow widths).
export const INSTAGRAM_ICON_WHITE_URL = `${runtimeEnvironment.assetBaseUrl}/insta-icon-128.png`;

// Public-facing app URL used when the "Link floor plans to LocateNow page"
// toggle is on — the email link points at `${PUBLIC_APP_URL}/fp/:id`.
// Falls back to the tab's origin at send time so dev/staging just works.
// REACT_APP_PUBLIC_APP_URL is mandatory and validated for every build.
const PUBLIC_APP_URL = runtimeEnvironment.publicAppUrl;

// ==================== Types ====================

export interface ClientInfo {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  max_budget?: number;
  min_bedrooms?: number;
  move_in_date?: string | null;
  move_in_timeline?: string;
  notes?: string;
  preferred_areas?: string[];
}

export interface GuestCardData {
  client: ClientInfo;
  recipientEmail: string;
  propertyName?: string;
  bedsBaths?: string;
  additionalComments?: string;
  locatorName: string;
  locatorPhone: string;
  locatorEmail: string;
  locatorJobTitle?: string;
  locatorPhotoUrl?: string;
  logoUrl?: string;
}

// Unit/Floor plan within a property
export interface ListingUnit {
  id: string;
  property_id: string;
  property_name: string;
  neighborhood?: string;
  model_name?: string;
  bedrooms: number;
  bathrooms: number;
  square_feet?: number;
  current_price: number;
  status?: string;
  available_date?: string;
  available_count?: number; // Set in floor-plan mode — number of units available for this floor plan
  leasing_special?: string;
  photo_url?: string;
  floorplan_url?: string;
  // DB UUID for floor_plans row. Used to build LocateNow /fp/:id URLs when
  // the useLocateNowFloorplanPage toggle is on; ignored otherwise.
  floor_plan_id?: string;
  property_url?: string;
  price_display?: string; // Pre-formatted price for floor plan mode (e.g. "$1,450 – $1,650")
  hide_square_feet_in_email?: boolean; // Local-only send override from SendListingsDialog
}

/** Shared by both MIME alternatives: never quote a known unavailable unit.
 * Legacy callers can omit status; preserve their existing numeric-price contract.
 * Missing/invalid prices and explicit unknown statuses require confirmation.
 */
export const listingPriceLabel = (unit: ListingUnit): string | null => {
  if (
    (unit.status != null && unit.status !== "available") ||
    unit.available_count === 0 ||
    !Number.isFinite(unit.current_price) || unit.current_price <= 0
  ) return null;
  return unit.price_display || `$${unit.current_price.toLocaleString()}`;
};

// Grouped property with multiple units
export interface GroupedProperty {
  property_id: string;
  property_name: string;
  neighborhood?: string;
  photo_url?: string;
  property_url?: string;
  leasing_special: string; // Editable per property
  units: ListingUnit[];
}

export interface ListingEmailData {
  client: ClientInfo;
  listings: ListingUnit[];
  groupedProperties?: GroupedProperty[]; // New: pre-grouped data
  locatorName: string;
  locatorPhone: string;
  locatorEmail: string;
  locatorJobTitle?: string;
  locatorPhotoUrl?: string; // Avatar URL from profiles table
  logoUrl?: string;
  customMessage?: string;
  selectionMode?: "floorplan" | "unit"; // Reserved for downstream template branching; plumbed from SendListingsDialog
  // Admin-only toggles from SendListingsDialog summary page. Default (undefined)
  // preserves today's behavior: images render when their URL is valid. Setting
  // either flag false force-hides that image class even when the URL exists.
  includePropertyImages?: boolean;
  includeFloorPlanImages?: boolean;
  // Tints the hero image with the identity band's color so image + band
  // feel like one unified header. Default (undefined) preserves today's
  // behavior: the tint is applied. Setting false leaves the hero photo
  // untinted — useful in light mode when the photography should pop.
  includeImageOverlays?: boolean;
  // When true, the hero tint is suppressed in light mode but still
  // applied in dark mode — so the photo pops on white backgrounds but
  // stays harmonized with the dark surround when the reader's client
  // renders dark mode. Default (undefined) = tint in both modes.
  overlaysDarkModeOnly?: boolean;
  // BETA toggle from SendListingsDialog. When true, floor-plan thumbnail
  // click-through points at the branded LocateNow /fp/:slug page instead
  // of the raw S3 image URL. Default/undefined preserves today's S3-link
  // behavior.
  useLocateNowFloorplanPage?: boolean;
  // floor_plan_id -> 6-char slug, minted by the send dialog via
  // POST /api/floorplan-tokens/batch just before render. When present,
  // the email link resolves to /fp/:slug (not /fp/:uuid) so the raw DB
  // id never leaves our infrastructure. Missing entry for a given
  // floor plan = that row's link stays on the S3 image URL (same as
  // toggle-off). Same-S3 fallback is per-row, never silent:
  // SendListingsDialog blocks the send entirely if minting failed.
  floorPlanSlugs?: Record<string, string>;
  // Footer action copy varies by send mode. Personal-mode sends should
  // invite the recipient to manage preferences; campaign mode keeps the
  // explicit unsubscribe language for one-click compliance.
  footerLinkKind?: "preferences" | "unsubscribe";
}

export interface EmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
  provider?: string;
  trackingId?: string;
}

export interface EmailConfig {
  agentName: string;
  agentEmail: string;
  agentPhone: string;
  companyName: string;
  jobTitle: string;
  logoUrl: string | null;
}

export interface EmailConfigIdentity {
  userId: string;
  generation: number;
}

export interface GmailStatus {
  connected: boolean;
  email: string | null;
  connected_at: string | null;
  last_used_at: string | null;
}

// ==================== Helper Functions ====================

/**
 * Group listings by property_id
 */
export function groupListingsByProperty(
  listings: ListingUnit[]
): GroupedProperty[] {
  const propertyMap = new Map<string, GroupedProperty>();

  listings.forEach((listing) => {
    const propertyId = listing.property_id || listing.id; // Fallback to id if no property_id

    if (!propertyMap.has(propertyId)) {
      propertyMap.set(propertyId, {
        property_id: propertyId,
        property_name: listing.property_name,
        neighborhood: listing.neighborhood,
        photo_url: listing.photo_url,
        property_url: listing.property_url,
        leasing_special: listing.leasing_special || "",
        units: [],
      });
    }

    propertyMap.get(propertyId)!.units.push(listing);
  });

  return Array.from(propertyMap.values());
}

/**
 * Format date for display
 * Shows "Available Now" if date is on or before today, otherwise shows the date
 */
function formatAvailableDate(dateStr?: string): string {
  if (!dateStr) return "Available Now";

  try {
    // API date-only values are calendar days, not UTC instants. Parsing
    // `YYYY-MM-DD` directly shifts them back one day in western timezones.
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    const date = dateOnlyMatch
      ? new Date(
          Number(dateOnlyMatch[1]),
          Number(dateOnlyMatch[2]) - 1,
          Number(dateOnlyMatch[3])
        )
      : new Date(dateStr);
    // If not a valid date (e.g. "3 available"), pass through as-is
    if (isNaN(date.getTime())) return dateStr;
    const now = new Date();

    // Compare dates only (ignore time)
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const todayOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    // If date is today or in the past, show "Available Now"
    if (dateOnly <= todayOnly) return "Available Now";

    // Format as MM/DD for future dates
    return `Available ${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}`;
  } catch {
    return "Available Now";
  }
}

/**
 * Check if a URL is valid and usable
 */
function isValidUrl(url?: string): boolean {
  if (!url) return false;
  const trimmed = url.trim();
  return (
    trimmed.length > 0 &&
    trimmed !== "#" &&
    trimmed !== "undefined" &&
    trimmed !== "null"
  );
}

/**
 * Escape HTML special characters to prevent XSS when interpolating
 * user-controlled data into HTML templates. Call this for any `${value}`
 * that lands inside HTML text content or attribute values.
 *
 * Returns "" for null/undefined so callers can interpolate directly
 * without an explicit fallback.
 */
function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value)
    .normalize("NFC")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Sanitize a URL for safe interpolation into an href/src attribute.
 * Rejects javascript:, data:, vbscript:, and any other non-allowlisted
 * scheme. Returns "#" for missing/invalid/unsafe URLs so the resulting
 * anchor is inert rather than dangerous.
 *
 * Allowed schemes: http, https, mailto, tel. Also permits scheme-less
 * paths (starts with `/` or `#`) or scheme-less bare strings (no `:`).
 *
 * Output is HTML-attribute-escaped so it can be dropped into href/src
 * directly without a second escapeHtml() wrap.
 */
function sanitizeUrl(url: unknown): string {
  if (url === null || url === undefined) return "#";
  const str = String(url).trim();
  if (!str) return "#";
  const safe =
    /^(https?:|mailto:|tel:|\/|#)/i.test(str) || !str.includes(":");
  if (!safe) return "#";
  return escapeHtml(str);
}

/**
 * Sanitize a string for use in a MIME header value (e.g. Subject,
 * From). Strips CR/LF to prevent header injection; collapses to a
 * single line. Gmail API handles MIME encoding downstream, so we only
 * need to block control-char injection here.
 */
function sanitizeHeader(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/[\r\n]+/g, " ").trim();
}

/**
 * Apply a preview color scheme override to email HTML for use in an
 * iframe. Used by EmailPreviewModal + SendListingsDialog so the
 * preview reflects exactly what a Light- or Dark-mode recipient sees,
 * regardless of the *viewer's* OS color-scheme setting.
 *
 * The CSS `color-scheme` property and `<meta name="color-scheme">`
 * tag control UA-rendered surfaces (scrollbars, form controls) but
 * do NOT override `@media (prefers-color-scheme: dark)` matching —
 * that media query reads the OS/UA preference directly. So the only
 * reliable way to override mode inside an iframe is to mutate the
 * HTML itself:
 *
 *   Light mode: strip every `@media (prefers-color-scheme: dark)
 *               { ... }` block from the HTML so it can't match.
 *   Dark mode:  leave those blocks alone AND inject their contents
 *               as unconditional CSS via forceEmailDarkMode() so
 *               the dark rules apply even when the viewer's OS is
 *               in light mode.
 */
export function applyEmailPreviewMode(
  html: string,
  mode: "light" | "dark"
): string {
  if (!html) return html;
  if (mode === "light") return stripPrefersDarkMediaBlocks(html);
  return forceEmailDarkMode(html);
}

/**
 * Remove every `@media (prefers-color-scheme: dark) { ... }` block
 * from the html. Walks braces by hand so nested rules survive (well,
 * they're removed along with their parent — but the surrounding CSS
 * stays intact). Returns the html unchanged when no such block exists.
 */
function stripPrefersDarkMediaBlocks(html: string): string {
  const marker = "@media";
  const prefersDark = /\(\s*prefers-color-scheme\s*:\s*dark\s*\)/;
  const segments: string[] = [];
  let cursor = 0;
  let i = 0;
  while (i < html.length) {
    const idx = html.indexOf(marker, i);
    if (idx === -1) break;
    const openBrace = html.indexOf("{", idx);
    if (openBrace === -1) break;
    const prelude = html.slice(idx, openBrace);
    if (!prefersDark.test(prelude)) {
      i = openBrace + 1;
      continue;
    }
    // Walk braces to find matching close.
    let depth = 1;
    let j = openBrace + 1;
    while (j < html.length && depth > 0) {
      const ch = html[j];
      if (ch === "{") depth++;
      else if (ch === "}") depth--;
      j++;
    }
    if (depth === 0) {
      // Append everything up to the start of this @media rule, skip
      // the rule, and continue past its closing brace.
      segments.push(html.slice(cursor, idx));
      cursor = j;
      i = j;
    } else {
      // Unbalanced braces — bail out and return what we have so far.
      break;
    }
  }
  if (segments.length === 0) return html;
  segments.push(html.slice(cursor));
  return segments.join("");
}

/**
 * Force the email's dark-mode rules on regardless of the viewer's OS
 * setting. Finds every `@media (prefers-color-scheme: dark) { ... }`
 * block in the email CSS and re-emits its contents as an unconditional
 * stylesheet appended just before </head>.
 *
 * Used as a primitive by applyEmailPreviewMode; preview surfaces
 * should call applyEmailPreviewMode(html, mode) instead of this
 * function directly so they get both halves of the toggle behavior
 * (light AND dark override) for free.
 *
 * Walks braces by hand rather than regex so nested rules (media
 * queries, keyframes, vendor blocks) work. Returns the html unchanged
 * when no dark-mode block is present.
 */
export function forceEmailDarkMode(html: string): string {
  const marker = "@media";
  const prefersDark = /\(\s*prefers-color-scheme\s*:\s*dark\s*\)/;
  const extracted: string[] = [];

  let i = 0;
  while (i < html.length) {
    const idx = html.indexOf(marker, i);
    if (idx === -1) break;
    // Find the opening brace of this @media rule
    const openBrace = html.indexOf("{", idx);
    if (openBrace === -1) break;
    const prelude = html.slice(idx, openBrace);
    if (!prefersDark.test(prelude)) {
      i = openBrace + 1;
      continue;
    }
    // Walk forward counting braces to find the matching close.
    let depth = 1;
    let j = openBrace + 1;
    while (j < html.length && depth > 0) {
      const ch = html[j];
      if (ch === "{") depth++;
      else if (ch === "}") depth--;
      j++;
    }
    // j is now one past the matching close brace.
    if (depth === 0) {
      extracted.push(html.slice(openBrace + 1, j - 1));
    }
    i = j;
  }

  if (extracted.length === 0) return html;

  const forceBlock = `<style data-email-dark-force="true">${extracted.join(
    "\n"
  )}</style>`;
  // Prefer inserting before </head> so it wins over the email's <style>
  // rules by source order. If no </head> tag exists (unlikely), fall
  // back to prepending to the body so it still takes effect.
  if (html.includes("</head>")) {
    return html.replace("</head>", `${forceBlock}</head>`);
  }
  return forceBlock + html;
}

// ==================== Email Templates ====================

export const generateGuestCardHtml = (data: GuestCardData): string => {
  const {
    client,
    bedsBaths,
    additionalComments,
    locatorName,
    locatorPhone,
    locatorEmail,
    locatorJobTitle,
    locatorPhotoUrl,
  } = data;

  const clientName = `${client.first_name} ${client.last_name}`;
  const budget = client.max_budget
    ? `$${client.max_budget.toLocaleString()}`
    : "Not specified";
  const moveIn = clientMoveIn(client) || 'Flexible';
  const phone = client.phone || "Not provided";
  const email = client.email || "Not provided";
  const bedsDisplay =
    bedsBaths ||
    (client.min_bedrooms ? `${client.min_bedrooms}+ Bed` : "Not specified");
  // NEVER fall back to client.notes: those are the operator's PRIVATE CRM
  // notes and this HTML ships to third-party leasing offices. Only what the
  // operator explicitly typed into the comments field may go out.
  const comments = additionalComments || "No additional comments";

  // Use locator photo from profiles, or fallback to default
  const locatorPhoto = locatorPhotoUrl || DEFAULT_LOCATOR_PHOTO_URL;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta name="x-apple-disable-message-reformatting" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <title>Client Guest Card</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style type="text/css">
    /* Basic email resets */
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      height: 100% !important;
      width: 100% !important;
    }
    body, table, td, a {
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
    table, td {
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
    }
    img {
      border: 0;
      outline: none;
      text-decoration: none;
      -ms-interpolation-mode: bicubic;
      display: block;
    }
    table {
      border-collapse: collapse !important;
    }
    body {
      margin: 0;
      padding: 0;
      background: #f7f8fc;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }
    a {
      color: #005dff;
    }

    /* Responsive helpers */
    @media only screen and (max-width: 600px) {
      .email-container {
        width: 100% !important;
        max-width: 100% !important;
      }
      .fluid {
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
      }
      .stack-column {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
      }
      .stack-column-left {
        padding-right: 0 !important;
      }
      .stack-column-right {
        padding-left: 0 !important;
        padding-top: 16px !important;
      }
      .mobile-px-24 {
        padding-left: 24px !important;
        padding-right: 24px !important;
      }
      .mobile-center {
        text-align: center !important;
      }
    }

    /* --------- DARK MODE SUPPORT ----------
     * Guest cards go to leasing offices, which often use Outlook. We
     * keep the dark-mode layer minimal — flip the page background and
     * the two main surfaces, let the client auto-invert text. Gmail
     * mirror via [data-ogsc] for Gmail web/Android. */
    @media (prefers-color-scheme: dark) {
      body, .page-bg {
        background: #18181b !important;
      }
      .email-container {
        background: #1f1f23 !important;
        box-shadow: none !important;
      }
      .client-info-card {
        background: #27272a !important;
      }
      .comments-box {
        background: #2a2a33 !important;
      }
      .guest-locator-card {
        background: #1f1f23 !important;
        border-color: #3f3f46 !important;
      }
    }
    [data-ogsc] body,
    [data-ogsc] .page-bg {
      background: #18181b !important;
    }
    [data-ogsc] .email-container {
      background: #1f1f23 !important;
      box-shadow: none !important;
    }
    [data-ogsc] .client-info-card {
      background: #27272a !important;
    }
    [data-ogsc] .comments-box {
      background: #2a2a33 !important;
    }
    [data-ogsc] .guest-locator-card {
      background: #1f1f23 !important;
      border-color: #3f3f46 !important;
    }
  </style>
</head>

<body style="margin: 0; padding: 0; background: #f7f8fc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <!-- FULL WIDTH WRAPPER -->
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" class="page-bg" style="background: #f7f8fc; padding: 24px 0;">
    <tr>
      <td align="center">
        <!-- OUTER CARD CONTAINER -->
        <table
          role="presentation"
          cellpadding="0"
          cellspacing="0"
          width="600"
          class="email-container"
          style="width: 600px; max-width: 600px; background: #ffffff; border-radius: 24px; box-shadow: 0 18px 40px rgba(15, 23, 42, 0.1);"
        >
          <tr>
            <td class="mobile-px-24" style="padding: 32px;">
              <!-- HEADER -->
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="text-align: center; padding-bottom: 24px;">
                    <h2 style="margin: 0; font-size: 26px; line-height: 1.3; color: #005dff; letter-spacing: 0.5px; font-weight: bold;">
                      Client Guest Card
                    </h2>
                  </td>
                </tr>
              </table>

              <!-- CLIENT INFO CARD WRAPPER (GREY, ROUNDED, SHADOW) -->
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td class="client-info-card" style="background: #f3f4f6; border-radius: 16px; box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1); padding: 18px 20px;">
                    <!-- INNER CONTENT TABLE -->
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <!-- LEFT COLUMN -->
                        <td class="stack-column stack-column-left" width="50%" valign="top" style="padding-right: 10px;">
                          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                            <!-- NAME -->
                            <tr>
                              <td style="padding-bottom: 10px;">
                                <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 2px;">
                                  Name
                                </div>
                                <div style="font-size: 15px; font-weight: 600; color: #111827;">
                                  ${escapeHtml(clientName)}
                                </div>
                              </td>
                            </tr>
                            <!-- PHONE -->
                            <tr>
                              <td style="padding-bottom: 10px;">
                                <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 2px;">
                                  Phone
                                </div>
                                <div style="font-size: 15px; color: #111827;">
                                  ${escapeHtml(phone)}
                                </div>
                              </td>
                            </tr>
                            <!-- EMAIL -->
                            <tr>
                              <td style="padding-bottom: 0;">
                                <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 2px;">
                                  Email
                                </div>
                                <div style="font-size: 15px; color: #111827;">
                                  ${escapeHtml(email)}
                                </div>
                              </td>
                            </tr>
                          </table>
                        </td>

                        <!-- RIGHT COLUMN -->
                        <td class="stack-column stack-column-right" width="50%" valign="top" style="padding-left: 10px;">
                          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                            <!-- MOVE-IN -->
                            <tr>
                              <td style="padding-bottom: 10px;">
                                <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 2px;">
                                  Move-In
                                </div>
                                <div style="font-size: 15px; color: #111827;">
                                  ${escapeHtml(moveIn)}
                                </div>
                              </td>
                            </tr>
                            <!-- BEDS/BATHS -->
                            <tr>
                              <td style="padding-bottom: 10px;">
                                <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 2px;">
                                  Beds/Baths
                                </div>
                                <div style="font-size: 15px; color: #111827;">
                                  ${escapeHtml(bedsDisplay)}
                                </div>
                              </td>
                            </tr>
                            <!-- BUDGET -->
                            <tr>
                              <td style="padding-bottom: 0;">
                                <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 2px;">
                                  Budget
                                </div>
                                <div style="font-size: 15px; font-weight: 600; color: #111827;">
                                  ${escapeHtml(budget)}
                                </div>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>

                      <!-- ADDITIONAL COMMENTS ROW -->
                      <tr>
                        <td colspan="2" style="padding-top: 14px;">
                          <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 6px;">
                            Additional Comments
                          </div>
                          <div class="comments-box" style="background: #e5e7f5; padding: 12px 14px; border-radius: 10px; color: #374151; font-size: 14px; line-height: 1.4;">
                            ${escapeHtml(comments)}
                          </div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>

              <!-- SPACER -->
              <div style="height: 24px; line-height: 24px; font-size: 0;">&nbsp;</div>

              <!-- LOCATOR HEADER -->
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="padding-top: 12px; padding-bottom: 8px;">
                    <h3 style="font-size: 18px; margin: 0; color: #005dff; border-bottom: 3px solid #ffd400; display: inline-block; padding-bottom: 4px;">
                      Locator Information
                    </h3>
                  </td>
                </tr>
              </table>

              <!-- LOCATOR CARD WRAPPER (WHITE, ROUNDED, SHADOW) -->
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td class="guest-locator-card" style="background: #ffffff; border-radius: 16px; border: 1px solid #e5e7eb; box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1); padding: 18px 20px;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <!-- LOCATOR PHOTO -->
                        <td width="72" valign="middle" style="padding-right: 14px;">
                          <img
                            src="${sanitizeUrl(locatorPhoto)}"
                            alt="${escapeHtml(locatorName)}"
                            style="width: 64px; height: 64px; border-radius: 50%; object-fit: cover; display: block;"
                          />
                        </td>

                        <!-- LOCATOR NAME + CONTACT -->
                        <td valign="middle">
                          <div style="font-size: 16px; font-weight: bold; color: #005dff; margin-bottom: 6px;">
                            ${escapeHtml(locatorName)}
                          </div>

                          <table role="presentation" cellpadding="0" cellspacing="0" style="font-size: 14px;">
                            <tr>
                              <td style="padding-bottom: 4px; white-space: nowrap;">
                                <span style="display: inline-block; vertical-align: middle; margin-right: 6px;">📞</span>
                                <a href="tel:${locatorPhone.replace(
                                  /[^0-9]/g,
                                  ""
                                )}" style="color: #005dff; text-decoration: none; vertical-align: middle;">${escapeHtml(locatorPhone)}</a>
                              </td>
                            </tr>
                            <tr>
                              <td style="white-space: nowrap;">
                                <span style="display: inline-block; vertical-align: middle; margin-right: 6px;">✉️</span>
                                <a href="${sanitizeUrl(`mailto:${locatorEmail}`)}" style="color: #005dff; text-decoration: none; vertical-align: middle;">${escapeHtml(locatorEmail)}</a>
                              </td>
                            </tr>
                          </table>
                        </td>

                        <!-- LOCATENOW LOGO -->
                        <td width="90" valign="middle" style="text-align: right;">
                          <img
                            src="${sanitizeUrl(COMPANY_LOGO_URL)}"
                            alt="${escapeHtml(COMPANY_NAME)}"
                            width="80"
                            style="max-width: 80px; height: auto; border-radius: 6px; display: block; margin-left: auto;"
                          />
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
        <!-- END OUTER CARD CONTAINER -->
      </td>
    </tr>
  </table>
</body>
</html>`;
};

/**
 * Generate the plain-text MIME alternative for listing sends.
 *
 * Built from the same ListingEmailData shape as generateListingEmailHtml
 * so the text and HTML versions stay in sync (Gmail penalizes
 * mismatched text/html pairs in Primary-tab placement).
 *
 * The {{UNSUBSCRIBE_URL}} placeholder is intentionally only in the HTML
 * version — backend substitutes there. Plain-text instructs the
 * recipient to use the HTML version's link or reply STOP. Postal
 * address is included literally for CAN-SPAM compliance in this
 * alternative too.
 */
export const generateListingEmailPlainText = (data: ListingEmailData): string => {
  const {
    client,
    groupedProperties,
    listings,
    customMessage,
    locatorName,
    locatorPhone,
    locatorEmail,
    locatorJobTitle,
    footerLinkKind,
  } = data;

  const properties =
    groupedProperties && groupedProperties.length > 0
      ? groupedProperties
      : groupListingsByProperty(listings);

  const lines: string[] = [];
  lines.push(`Hey ${client.first_name || "there"},`);
  lines.push("");
  if (customMessage?.trim()) {
    lines.push(customMessage.trim());
    lines.push("");
  }
  const propLabel = properties.length === 1 ? "property" : "properties";
  lines.push(
    `Here ${properties.length === 1 ? "is" : "are"} ${properties.length} ${propLabel} we think you'll like:`,
  );
  lines.push("");
  for (const property of properties) {
    const neighborhood = property.neighborhood ? ` (${property.neighborhood})` : "";
    lines.push(`* ${property.property_name}${neighborhood}`);
    if (property.leasing_special) {
      lines.push(`  Special: ${property.leasing_special}`);
    }
    for (const unit of property.units) {
      const sqft =
        unit.square_feet && !unit.hide_square_feet_in_email
          ? ` · ${unit.square_feet.toLocaleString()} sqft`
          : "";
      const model = unit.model_name || "Unit";
      const price = listingPriceLabel(unit);
      lines.push(
        `  - ${model}: ${unit.bedrooms}BR/${unit.bathrooms}BA${sqft} · ${price ? `${price}/mo` : "Confirm pricing"}`,
      );
    }
    if (property.property_url) lines.push(`  ${property.property_url}`);
    lines.push("");
  }
  lines.push("---");
  if (locatorName) lines.push(locatorName);
  if (locatorJobTitle) lines.push(locatorJobTitle);
  if (locatorPhone) lines.push(locatorPhone);
  if (locatorEmail) lines.push(locatorEmail);
  lines.push("");
  lines.push(
    footerLinkKind === "preferences"
      ? "Want fewer emails? Use the manage preferences link in the HTML version, or reply STOP."
      : "Don't want these emails? Use the unsubscribe link in the HTML version, or reply STOP.",
  );
  lines.push("Affiliated with Real McCoy Realty Consultants");
  lines.push("9615 Stoney Ridge Lane, Johns Creek, GA 30002");
  return lines.join("\n");
};

/**
 * Generate listing email HTML with properties grouped
 */
export const generateListingEmailHtml = (data: ListingEmailData): string => {
  const {
    client,
    listings,
    groupedProperties,
    locatorName,
    locatorPhone,
    locatorEmail,
    locatorJobTitle,
    locatorPhotoUrl,
    customMessage,
    includePropertyImages,
    includeFloorPlanImages,
    includeImageOverlays,
    overlaysDarkModeOnly,
    useLocateNowFloorplanPage,
    floorPlanSlugs,
    footerLinkKind,
  } = data;

  // Default both image classes ON when flags are undefined (preserves today's
  // behavior for any call site that doesn't know about the admin toggles).
  // Only explicit `false` force-hides images.
  const showPropertyImages = includePropertyImages !== false;
  const showFloorPlanImages = includeFloorPlanImages !== false;
  // Same default-on semantics for the hero tint overlay — the admin can
  // flip it off per-send from the email dialog's summary page.
  const showImageOverlays = includeImageOverlays !== false;
  // When true, the light-mode inline tint is omitted (and only the
  // dark-mode CSS rule paints the overlay). Moot when showImageOverlays
  // is false — the overlay is off in both modes anyway.
  const tintDarkOnly = overlaysDarkModeOnly === true;

  // Use pre-grouped data if available, otherwise group the listings
  const properties = groupedProperties || groupListingsByProperty(listings);

  // Use locator photo from profiles, or fallback to default
  const locatorPhoto = locatorPhotoUrl || DEFAULT_LOCATOR_PHOTO_URL;

  const greeting =
    customMessage || `A quick snapshot of a few places we think you'll like.`;
  const footerActionLabel =
    footerLinkKind === "preferences" ? "Manage email preferences" : "Unsubscribe";
  const footerIntroCopy =
    footerLinkKind === "preferences"
      ? "Want fewer emails?"
      : "Don't want these emails?";

  // Generate HTML for each property card
  const propertiesHtml = properties
    .map((property) => {
      // No stock-photo fallbacks: if the real photo is missing, we hide the
      // image cell entirely rather than showing an unrelated Unsplash image.
      // Admin can also force-hide the hero via the "Include property images"
      // toggle on the send dialog's summary page.
      const hasPhoto = showPropertyImages && isValidUrl(property.photo_url);
      const photoUrl = property.photo_url || "";

      // Check if we have a valid property URL
      const hasValidPropertyUrl = isValidUrl(property.property_url);

      // Only use neighborhood if it exists and isn't empty
      const neighborhood = property.neighborhood?.trim();

      // Generate floor plan rows
      const floorplansHtml = property.units
        .map((unit) => {
          // No stock-photo fallbacks: if no floor plan image exists, we hide
          // the thumbnail cell and the "View Floor Plan" link rather than
          // point at an unrelated Unsplash image. Admin can also force-hide
          // all thumbs via the "Include floor plan images" toggle on the
          // send dialog's summary page.
          const hasThumb = showFloorPlanImages && isValidUrl(unit.floorplan_url);
          const thumbUrl = unit.floorplan_url || "";
          const modelName = unit.model_name || "Unit";
          const price = listingPriceLabel(unit);
          const availability = formatAvailableDate(unit.available_date);
          const sqft = !unit.hide_square_feet_in_email && unit.square_feet
            ? `${unit.square_feet.toLocaleString()} sq ft`
            : "";
          // Floor-plan mode sets available_count as a structured number.
          // Render "N unit(s) · " so the count is clear without implying that
          // all N are available on the soonest date — the date is the soonest
          // across the pool; other units may be later.
          const availabilityChip = unit.available_count
            ? `${unit.available_count} unit${unit.available_count === 1 ? "" : "s"} · `
            : "";

          const safeThumbUrl = sanitizeUrl(thumbUrl);
          // Link destination branches on the "Link floor plans to LocateNow
          // page" toggle. <img src> always stays on S3 because email clients
          // need direct image URLs.
          //
          // Precedence when toggle is ON:
          //   1. floorPlanSlugs[floor_plan_id] → /fp/:slug (preferred,
          //      short opaque token, expiring, revocable).
          //   2. floor_plan_id direct → /fp/:uuid (fallback for rows the
          //      mint couldn't cover, e.g. a floor plan missing from
          //      the batch response).
          //   3. S3 thumb URL → original behavior (toggle off or no
          //      floor_plan_id at all).
          //
          // Per-row fallback to S3 is safe here because
          // SendListingsDialog blocks the send entirely if the mint
          // fails — a missing slug map reaches this code only when the
          // caller didn't request one (toggle off).
          const slug = unit.floor_plan_id && floorPlanSlugs
            ? floorPlanSlugs[unit.floor_plan_id]
            : undefined;
          // If the toggle is on and we had a floor_plan_id but no slug,
          // we're about to emit a raw UUID URL — exactly the leak this
          // feature was built to prevent. Shouldn't happen (the dialog
          // blocks send on mint failure and mints for every id in the
          // listing set), but warn loudly if it ever fires so Sentry /
          // ops see it immediately rather than "surprise, we regressed."
          if (
            useLocateNowFloorplanPage === true &&
            !slug &&
            unit.floor_plan_id &&
            PUBLIC_APP_URL
          ) {
            // eslint-disable-next-line no-console
            console.warn("floorplan_link_uuid_fallback", {
              floor_plan_id: unit.floor_plan_id,
            });
          }
          const linkHref =
            useLocateNowFloorplanPage === true && PUBLIC_APP_URL && slug
              ? sanitizeUrl(`${PUBLIC_APP_URL}/fp/${encodeURIComponent(slug)}`)
              : useLocateNowFloorplanPage === true && PUBLIC_APP_URL && unit.floor_plan_id
                ? sanitizeUrl(`${PUBLIC_APP_URL}/fp/${encodeURIComponent(unit.floor_plan_id)}`)
                : safeThumbUrl;
          const thumbCell = hasThumb
            ? `<td width="60" valign="top" style="padding-right: 12px;">
                            <a href="${linkHref}" target="_blank" style="display:block;">
                              <img
                                style="width: 56px; height: 56px; border-radius: 6px; display: block; object-fit: cover; cursor: pointer;"
                                src="${safeThumbUrl}"
                                alt="${escapeHtml(modelName)}"
                              />
                            </a>
                          </td>`
            : "";
          const viewFloorPlanLink = hasThumb
            ? `<a href="${linkHref}" target="_blank" style="font-size: 11px; color: #005dff; text-decoration: none; font-weight: 500;">
                              View Floor Plan →
                            </a>`
            : "";

          return `
                  <!-- FLOORPLAN ROW -->
                  <tr class="floorplan-row">
                    <td style="padding: 12px 16px; border-top: 1px solid #e5e7eb;">
                      <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                        <tr>
                          <!-- Thumbnail (omitted when no floor plan image) -->
                          ${thumbCell}
                          <!-- Details -->
                          <td valign="middle">
                            <div class="unit-model" style="font-size: 14px; font-weight: 600; color: #111827; margin-bottom: 2px;">
                              ${escapeHtml(modelName)} <span class="unit-model-meta" style="font-weight: 400; color: #6b7280;">· ${
            unit.bedrooms
          }bd/${unit.bathrooms}ba</span>
                            </div>
                            <div class="unit-sub" style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">
                              ${sqft}${sqft ? " · " : ""}${availabilityChip}${escapeHtml(availability)}
                            </div>
                            ${viewFloorPlanLink}
                          </td>
                          <!-- Price.
                               Width bumped from 100 to 140 + nowrap on
                               the value so price ranges like "$2,412 –
                               $2,602" stay on one line instead of
                               wrapping at the en-dash. Single values
                               (~$2,557, $1,895/month) still right-align
                               cleanly within the wider cell. -->
                          <td width="140" align="right" valign="middle" style="white-space: nowrap;">
                            <div style="font-size: 16px; font-weight: 700; color: #005dff; white-space: nowrap;">
                              ${escapeHtml(price || "Confirm pricing")}
                            </div>
                            <div style="font-size: 11px; color: #6b7280;">${price && !unit.price_display ? "/month" : ""}</div>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>`;
        })
        .join("\n");

      // Leasing special section (only if exists, not empty, and not "N/A")
      const leasingSpecialValue = property.leasing_special?.trim();
      const hasValidLeasingSpecial =
        !!leasingSpecialValue &&
        leasingSpecialValue.toLowerCase() !== "n/a" &&
        leasingSpecialValue.toLowerCase() !== "na" &&
        leasingSpecialValue !== "-";
      // Neighborhood overlays on top-right of the hero image (desktop+mobile).
      const desktopNeighborhoodOverlayHtml = neighborhood
        ? `<div class="desktop-neighborhood-overlay" style="line-height: 1;">
                              <span class="desktop-neighborhood-chip neighborhood-pill" style="display: inline-block; background: #1e293b; color: #93c5fd; font-size: 12px; font-weight: 600; padding: 6px 12px; border-radius: 9999px;">${escapeHtml(neighborhood)}</span>
                            </div>`
        : "";
      const mobileNeighborhoodOverlayHtml = neighborhood
        ? `<div class="mobile-neighborhood-overlay" style="line-height: 1;">
                              <span class="mobile-neighborhood-chip neighborhood-pill" style="display: inline-block; background: #1e293b; color: #93c5fd; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 9999px;">${escapeHtml(neighborhood)}</span>
                            </div>`
        : "";

      // "Current Special:" callout style used as a bottom-left image overlay.
      const desktopOverlaySpecialHtml = hasValidLeasingSpecial
        ? `<div class="desktop-overlay-special leasing-special-box" style="display: inline-block; max-width: 340px; background: rgba(255,248,235,0.95); border-left: 4px solid #f59e0b; border-radius: 8px; padding: 7px 10px; color: #7c2d12; font-size: 13px; line-height: 1.3;">
                              <strong class="leasing-special-label" style="color: #b45309; font-weight: 700;">Current Special:</strong> ${escapeHtml(property.leasing_special)}
                            </div>`
        : "";
      const mobileOverlaySpecialHtml = hasValidLeasingSpecial
        ? `<div class="mobile-overlay-special leasing-special-box" style="display: inline-block; max-width: 86%; background: rgba(255,248,235,0.96); border-left: 3px solid #f59e0b; border-radius: 7px; padding: 6px 8px; color: #7c2d12; font-size: 12px; line-height: 1.28;">
                              <strong class="leasing-special-label" style="color: #b45309; font-weight: 700;">Current Special:</strong> ${escapeHtml(property.leasing_special)}
                            </div>`
        : "";

      // Span-based "View Property" label lives inside an outer <a> that
      // wraps the whole band (property name + this label), so the label is
      // a <span> not an <a>. Keeps the band a single clickable unit.
      const bandViewLabelHtml = hasValidPropertyUrl
        ? `<span class="band-view-link" style="color: #ffffff; font-size: 13px; font-weight: 700; text-decoration: none; white-space: nowrap;">View&nbsp;Property&nbsp;→</span>`
        : "";

      const desktopBandViewLabelHtml = hasValidPropertyUrl
        ? `<span class="desktop-band-view-link" style="color: #2563eb; font-size: 18px; font-weight: 700; text-decoration: none; white-space: nowrap;">View Property →</span>`
        : "";

      // DESKTOP hero: full-width image rendered as a <td background> with
      // overlay content layered on top (neighborhood top-right, special
      // bottom-left). This is the "bulletproof background image" pattern —
      // reliable in Gmail, Apple Mail, iOS/Android Mail, Yahoo, with VML
      // fallback for Outlook desktop. Previous negative-margin approach
      // was failing in some clients (overlays dropped below the image).
      const safePhotoUrl = sanitizeUrl(photoUrl);
      const desktopHeroCellInnerHtml = `
                            <!--[if gte mso 9]>
                            <v:rect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" fill="true" stroke="false" style="width:600px;height:156px;">
                              <v:fill type="frame" src="${safePhotoUrl}" color="#eef3ff" />
                              <v:textbox inset="0,0,0,0">
                            <![endif]-->
                            <table width="100%" cellpadding="0" cellspacing="0" role="presentation" class="desktop-image-overlay-layer${showImageOverlays ? "" : " overlay-tint-off"}" style="width: 100%; height: 156px;${showImageOverlays && !tintDarkOnly ? " background-color: rgba(238,243,255,0.30);" : ""}">
                              <tr>
                                <td align="left" valign="top" style="padding: 10px 6px 0 10px;">
                                  ${desktopOverlaySpecialHtml}
                                </td>
                                <td align="right" valign="top" style="padding: 10px 10px 0 6px; white-space: nowrap;">
                                  ${desktopNeighborhoodOverlayHtml}
                                </td>
                              </tr>
                            </table>
                            <!--[if gte mso 9]>
                              </v:textbox>
                            </v:rect>
                            <![endif]-->`;
      const desktopHeroCellHtml = `<td height="156" background="${safePhotoUrl}" bgcolor="#eef3ff" valign="top" class="desktop-hero-bg-cell" style="background: url('${safePhotoUrl}') no-repeat center / cover #eef3ff; height: 156px; border-radius: 12px 12px 0 0; padding: 0; line-height: 0; font-size: 0;">${desktopHeroCellInnerHtml}</td>`;
      const desktopHeroImageHtml = hasPhoto
        ? hasValidPropertyUrl
          ? `<a href="${sanitizeUrl(property.property_url)}" target="_blank" style="display: block; text-decoration: none; color: inherit; line-height: 0;"><table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="width: 100%;"><tr>${desktopHeroCellHtml}</tr></table></a>`
          : `<table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="width: 100%;"><tr>${desktopHeroCellHtml}</tr></table>`
        : "";
      const desktopBandFallbackSpecialHtml =
        !hasPhoto && hasValidLeasingSpecial
          ? `<div class="desktop-band-fallback-special leasing-special-box" style="margin-top: 8px; display: inline-block; max-width: 340px; background: #fff8eb; border-left: 4px solid #f59e0b; border-radius: 8px; padding: 7px 10px; color: #7c2d12; font-size: 13px; line-height: 1.3;">
                                      <strong class="leasing-special-label" style="color: #b45309; font-weight: 700;">Current Special:</strong> ${escapeHtml(property.leasing_special)}
                                    </div>`
          : "";
      // Desktop identity band — the entire band (property name + "View
      // Property →") is a single <a> so clicking anywhere opens the
      // listing. When there's no property URL we fall back to a plain div.
      const desktopBandInnerHtml = `<table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                                  <tr>
                                    <td valign="middle">
                                      <div class="desktop-band-property-name property-name" style="color: #0f172a; font-size: 22px; font-weight: 700; line-height: 1.2; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                        ${escapeHtml(property.property_name)}
                                      </div>
                                      ${desktopBandFallbackSpecialHtml}
                                    </td>
                                    ${
                                      desktopBandViewLabelHtml
                                        ? `<td align="right" valign="middle" style="padding-left: 14px; text-align: right; white-space: nowrap;">
                                      ${desktopBandViewLabelHtml}
                                    </td>`
                                        : ""
                                    }
                                  </tr>
                                </table>`;
      const desktopBandWrappedHtml = hasValidPropertyUrl
        ? `<a href="${sanitizeUrl(property.property_url)}" target="_blank" class="desktop-band-link" style="display: block; text-decoration: none; color: inherit; padding: 12px 16px;">${desktopBandInnerHtml}</a>`
        : `<div style="padding: 12px 16px;">${desktopBandInnerHtml}</div>`;

      const desktopHeaderHeroRowsHtml = `
                            ${
                              hasPhoto
                                ? `<tr class="desktop-hero-image-row">
                              <td style="padding: 0; line-height: 0; font-size: 0;">
                                ${desktopHeroImageHtml}
                              </td>
                            </tr>`
                                : ""
                            }
                            <tr class="desktop-hero-band-row">
                              <td class="desktop-hero-band-bg" style="background: #eef3ff; padding: 0; border-radius: 0;">
                                ${desktopBandWrappedHtml}
                              </td>
                            </tr>
                            `;

      // MOBILE hero — same bulletproof <td background> overlay approach
      // as desktop. Rounded top corners only; bottom corners absorbed by
      // the identity band below so image + band read as one shape.
      const mobileHeroCellInnerHtml = `
                        <!--[if gte mso 9]>
                        <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:600px;height:142px;">
                          <v:fill type="frame" src="${safePhotoUrl}" color="#111827" />
                          <v:textbox inset="0,0,0,0">
                        <![endif]-->
                        <table width="100%" cellpadding="0" cellspacing="0" role="presentation" class="mobile-image-overlay-layer${showImageOverlays ? "" : " overlay-tint-off"}" style="width: 100%; height: 142px;${showImageOverlays && !tintDarkOnly ? " background-color: rgba(17,24,39,0.30);" : ""}">
                          <tr>
                            <td align="left" valign="top" style="padding: 8px 4px 0 8px;">
                              ${mobileOverlaySpecialHtml}
                            </td>
                            <td align="right" valign="top" style="padding: 8px 8px 0 4px; white-space: nowrap;">
                              ${mobileNeighborhoodOverlayHtml}
                            </td>
                          </tr>
                        </table>
                        <!--[if gte mso 9]>
                          </v:textbox>
                        </v:rect>
                        <![endif]-->`;
      const mobileHeroCellHtml = `<td height="142" background="${safePhotoUrl}" bgcolor="#111827" valign="top" class="mobile-hero-bg-cell" style="background: url('${safePhotoUrl}') no-repeat center / cover #111827; height: 142px; border-radius: 12px 12px 0 0; padding: 0; line-height: 0; font-size: 0;">${mobileHeroCellInnerHtml}</td>`;
      const mobileImageInner = `<table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="width: 100%;"><tr>${mobileHeroCellHtml}</tr></table>`;
      const mobileImageRowHtml = hasPhoto
        ? `
                  <!-- MOBILE HERO IMAGE (hidden on desktop) -->
                  <tr class="mobile-image-row">
                    <td style="padding: 0; line-height: 0; font-size: 0;">
                      ${
                        hasValidPropertyUrl
                          ? `<a href="${sanitizeUrl(property.property_url)}" target="_blank" style="display: block; text-decoration: none; line-height: 0;">${mobileImageInner}</a>`
                          : mobileImageInner
                      }
                    </td>
                  </tr>`
        : "";

      // MOBILE identity band — attached directly under the image with zero
      // gap. Dark background in both light/dark mode (intentional: the
      // image is always visually "warm" so a dark band always reads).
      // When no photo is present the band still works as a card header
      // (radius top corners instead of bottom-only). Entire band wraps
      // in a single <a> so tapping anywhere opens the listing.
      const mobileBandInnerHtml = `<table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                        <tr>
                          <td valign="middle">
                            <div class="band-property-name" style="color: #ffffff; font-size: 17px; font-weight: 700; line-height: 1.25; margin: 0 0 6px 0;">
                              ${escapeHtml(property.property_name)}
                            </div>
                            ${
                              !hasPhoto && hasValidLeasingSpecial
                                ? `<div class="mobile-band-fallback-special leasing-special-box" style="margin-top: 6px; display: inline-block; border-left: 3px solid #f59e0b; padding-left: 8px; color: #fde68a; font-size: 12px; line-height: 1.3;">
                                    <strong class="leasing-special-label" style="color: #fbbf24; font-weight: 700;">Current Special:</strong> ${escapeHtml(property.leasing_special)}
                                  </div>`
                                : ""
                            }
                          </td>
                          ${
                            bandViewLabelHtml
                              ? `<td align="right" valign="middle" style="padding-left: 8px; white-space: nowrap;">
                                ${bandViewLabelHtml}
                              </td>`
                              : ""
                          }
                        </tr>
                      </table>`;
      const mobileBandWrappedHtml = hasValidPropertyUrl
        ? `<a href="${sanitizeUrl(property.property_url)}" target="_blank" class="mobile-band-link" style="display: block; text-decoration: none; color: inherit; padding: 12px 14px;">${mobileBandInnerHtml}</a>`
        : `<div style="padding: 12px 14px;">${mobileBandInnerHtml}</div>`;
      const mobileIdentityBandHtml = `
                  <!-- MOBILE IDENTITY BAND (hidden on desktop) -->
                  <tr class="mobile-identity-band">
                    <td class="identity-band-td" style="background: #111827; padding: 0; ${
                      hasPhoto ? "border-radius: 0 0 0 0;" : "border-radius: 12px 12px 0 0;"
                    }">
                      ${mobileBandWrappedHtml}
                    </td>
                  </tr>`;

      return `
            <!-- PROPERTY CARD: ${escapeHtml(property.property_name)} -->
            <tr>
              <td style="padding: 0 24px 20px 24px;" class="inner-pad property-card-wrap">
                <table
                  width="100%"
                  cellpadding="0"
                  cellspacing="0"
                  style="border: 1px solid #e5e7eb; border-radius: 12px; background: #ffffff; overflow: hidden;"
                  class="property-card"
                  role="presentation"
                >
                  ${mobileImageRowHtml}
                  ${mobileIdentityBandHtml}

                  <!-- DESKTOP HEADER (hidden on mobile) -->
                  <tr class="desktop-header-row">
                    <td style="padding: 0;">
                      <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                        ${desktopHeaderHeroRowsHtml}
                      </table>
                    </td>
                  </tr>

                  <!-- FLOOR PLANS -->
                  <tr>
                    <td class="floor-plans-bg" style="background: #f9fafb;">
                      <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                        ${floorplansHtml}
                      </table>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>`;
    })
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="x-apple-disable-message-reformatting" />
    <meta name="color-scheme" content="light dark" />
    <meta name="supported-color-schemes" content="light dark" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <title>LocateNow Apartment List</title>
    <!--[if mso]>
    <noscript>
      <xml>
        <o:OfficeDocumentSettings>
          <o:PixelsPerInch>96</o:PixelsPerInch>
        </o:OfficeDocumentSettings>
      </xml>
    </noscript>
    <![endif]-->
    <style>
      /* Reset */
      body, table, td, p, a, li, blockquote {
        -webkit-text-size-adjust: 100%;
        -ms-text-size-adjust: 100%;
      }
      table, td {
        mso-table-lspace: 0pt;
        mso-table-rspace: 0pt;
      }
      img {
        -ms-interpolation-mode: bicubic;
        border: 0;
        height: auto;
        line-height: 100%;
        outline: none;
        text-decoration: none;
      }
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: #f7f8fc;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      }
      table {
        border-collapse: collapse !important;
      }
      a {
        color: #005dff;
      }
      .locator-insta-band-link,
      .locator-insta-band-link .locator-instagram-handle {
        color: #ffffff !important;
      }

      /* --------- DEFAULT (DESKTOP) VISIBILITY FOR UNIFIED CARD ---------
       * Mobile rows (image + identity band) are hidden by default so the
       * desktop side-by-side header is the only thing that renders on
       * >600px. The @media block below flips this for mobile. */
      .mobile-image-row,
      .mobile-identity-band {
        display: none;
      }
      .desktop-header-row {
        display: table-row;
      }

      /* Admin toggle ("Include image overlays" off) — strip the tint in
       * every mode. Compound selector out-specificities the plain class
       * rules inside @media dark / [data-ogsc] blocks below. */
      .desktop-image-overlay-layer.overlay-tint-off,
      .mobile-image-overlay-layer.overlay-tint-off {
        background-color: transparent !important;
      }

      /* --------- MOBILE RESPONSIVE ---------- */
      @media only screen and (max-width: 600px) {
        .main-wrapper {
          padding: 12px 0 !important;
        }
        .main-card {
          border-radius: 0 !important;
          box-shadow: none !important;
        }
        .inner-pad {
          padding-left: 16px !important;
          padding-right: 16px !important;
        }
        /* Property card edge-to-edge-ish on mobile: tighter side padding
         * so the image/band takes full visible width of the card. */
        .property-card-wrap {
          padding-left: 12px !important;
          padding-right: 12px !important;
        }
        /* Unified-card visibility swap: hide desktop header, show mobile
         * image + identity band rows. */
        .desktop-header-row {
          display: none !important;
        }
        .mobile-image-row {
          display: table-row !important;
        }
        .mobile-identity-band {
          display: table-row !important;
        }
        /* Brand band: tighter still on mobile — compact header with an
         * 80px logo so the property card is the first thing the eye sees
         * inside the viewport. */
        .brand-band {
          padding: 10px 16px !important;
        }
        .brand-band img {
          width: 80px !important;
        }
        /* Keep horizontal layout on mobile - just adjust sizes */
        .locator-photo-cell {
          width: 60px !important;
          padding-right: 12px !important;
        }
        .locator-photo-cell img {
          width: 50px !important;
          height: 50px !important;
        }
        .locator-info-cell p {
          font-size: 14px !important;
        }
        /* Disclosure drops another step on mobile so the 175-char line
         * still lands in two full lines instead of wrapping to three. */
        .pricing-disclosure {
          font-size: 11px !important;
          line-height: 1.45 !important;
        }
        /* Insta gradient band — slightly tighter vertical padding on
         * mobile so the whole locator card stays compact. Icon hides
         * on narrow widths so the "Follow us · @handle →" line still
         * fits without wrapping. */
        .locator-insta-band-link {
          padding: 11px 16px !important;
          font-size: 13px !important;
        }
        .locator-insta-icon {
          display: none !important;
        }
        /* "Just reach out" subcopy drops another step on mobile to keep
         * the one-line rhythm going on narrower screens. */
        .section-subcopy {
          font-size: 11px !important;
          line-height: 1.45 !important;
        }
        /* Make floor plan thumbnails more tappable on mobile */
        .floorplan-thumb {
          width: 50px !important;
          height: 50px !important;
        }
      }

      /* --------- DARK MODE SUPPORT ----------
       * Coverage target: every surface the template ships must flip.
       * Light-mode-only elements (hardcoded #f5f5f7, #eef1fb, etc.)
       * looked broken in dark mode in prior iterations — we now override
       * each one explicitly with readable foreground colors.
       *
       * Gmail desktop/web strips @media rules, so every rule below is
       * duplicated as a [data-ogsc] selector, which Gmail sets on the
       * root element when its UI is in dark mode. Apple Mail, iOS Mail,
       * and Gmail iOS honor @media natively.
       *
       * Note on the logo: it lives on a solid blue .brand-band in both
       * light and dark mode, so there is no logo swap. The previous
       * .logo-light / .logo-dark swap rules (and their [data-ogsc]
       * mirrors) have been deleted — they were a fragile workaround
       * for a problem the band removes entirely.
       */
      @media (prefers-color-scheme: dark) {
        body, .main-wrapper {
          background: #18181b !important;
        }
        .main-card {
          background: #1f1f23 !important;
          box-shadow: none !important;
        }

        /* Property card + contents */
        .property-card {
          background: #27272a !important;
          border-color: #3f3f46 !important;
        }
        .property-name {
          color: #f4f4f5 !important;
        }
        /* Desktop identity band flips from light-blue to a dark slate in
         * dark mode. Without this, the band stays #eef3ff and the
         * property name (which dark-mode flips to near-white) becomes
         * invisible against it. */
        .desktop-hero-band-bg {
          background: #1e293b !important;
        }
        .desktop-band-view-link {
          color: #93c5fd !important;
        }
        /* Image tint matches the color of the band directly below the
         * image so hero + band read as one cohesive header unit. Desktop
         * band in dark mode is slate-800 (#1e293b); mobile band is
         * always #111827 regardless of mode. */
        .desktop-image-overlay-layer {
          background-color: rgba(30, 41, 59, 0.45) !important;
        }
        .mobile-image-overlay-layer {
          background-color: rgba(17, 24, 39, 0.45) !important;
        }
        .floor-plans-bg {
          background: #1e1e22 !important;
        }
        .floorplan-row {
          border-top-color: #3f3f46 !important;
        }
        .unit-model {
          color: #f4f4f5 !important;
        }
        .unit-model-meta,
        .unit-sub {
          color: #a1a1aa !important;
        }

        /* Neighborhood pill + special callout + disclosure */
        .neighborhood-pill {
          background: #1e293b !important;
          color: #93c5fd !important;
        }
        /* Soft-style special block — cream bg in light mode flips to a
         * muted dark surface in dark mode. Stacked background: a 20%
         * black veil layered on top of the warm amber base, so the
         * whole chip (including text) reads as dimmed/veiled instead
         * of popping out as a bright splash against the dark body.
         * Text colors also stepped down a notch so they don't fight
         * back through the veil. */
        .leasing-special-box {
          background: linear-gradient(rgba(0,0,0,0.20), rgba(0,0,0,0.20)), rgba(20, 16, 8, 0.85) !important;
          color: #e8c96e !important;
        }
        .leasing-special-label {
          color: #d4851a !important;
        }
        .pricing-disclosure {
          color: #a1a1aa !important;
        }
        .footer-credit,
        .footer-unsubscribe {
          color: #71717a !important;
        }
        .footer-unsubscribe a {
          color: #71717a !important;
        }

        /* Tour-ready heading + supporting copy */
        .section-heading {
          color: #93c5fd !important;
        }
        .section-subcopy {
          color: #a1a1aa !important;
        }

        /* Locator card at the bottom — bumped bg a step brighter than
         * the body (#18181b) so the card reads as a distinct container
         * instead of blending into the email body. Border also bumped
         * to a more visible slate. */
        .locator-card {
          background: #27272a !important;
          border-color: #52525b !important;
        }
        .locator-name {
          color: #93c5fd !important;
        }
        .locator-title {
          color: #a1a1aa !important;
        }
        .locator-company {
          color: #d4d4d8 !important;
        }
        .locator-link {
          color: #93c5fd !important;
        }
        .locator-insta-band-link,
        .locator-insta-band-link .locator-instagram-handle {
          color: #ffffff !important;
        }
      }

      /* Mirror of the rules above for Gmail dark mode. Different Gmail
       * surfaces use data-ogsc and data-ogsb in the wild. The logo swap
       * rules previously mirrored here are gone — brand band handles
       * contrast in every mode. Every other @media rule above should
       * still have a matching non-@media fallback here. */
      [data-ogsc] body,
      [data-ogsc] .main-wrapper {
        background: #18181b !important;
      }
      [data-ogsc] .main-card {
        background: #1f1f23 !important;
        box-shadow: none !important;
      }
      [data-ogsc] .property-card {
        background: #27272a !important;
        border-color: #3f3f46 !important;
      }
      [data-ogsc] .property-name {
        color: #f4f4f5 !important;
      }
      [data-ogsc] .desktop-hero-band-bg {
        background: #1e293b !important;
      }
      [data-ogsc] .desktop-band-view-link {
        color: #93c5fd !important;
      }
      [data-ogsc] .desktop-image-overlay-layer {
        background-color: rgba(30, 41, 59, 0.45) !important;
      }
      [data-ogsc] .mobile-image-overlay-layer {
        background-color: rgba(17, 24, 39, 0.45) !important;
      }
      [data-ogsc] .floor-plans-bg {
        background: #1e1e22 !important;
      }
      [data-ogsc] .floorplan-row {
        border-top-color: #3f3f46 !important;
      }
      [data-ogsc] .unit-model {
        color: #f4f4f5 !important;
      }
      [data-ogsc] .unit-model-meta,
      [data-ogsc] .unit-sub {
        color: #a1a1aa !important;
      }
      [data-ogsc] .neighborhood-pill {
        background: #1e293b !important;
        color: #93c5fd !important;
      }
      [data-ogsc] .leasing-special-box {
        background: linear-gradient(rgba(0,0,0,0.20), rgba(0,0,0,0.20)), rgba(20, 16, 8, 0.85) !important;
        color: #e8c96e !important;
      }
      [data-ogsc] .leasing-special-label {
        color: #d4851a !important;
      }
      [data-ogsc] .pricing-disclosure {
        color: #a1a1aa !important;
      }
      [data-ogsc] .footer-credit,
      [data-ogsc] .footer-unsubscribe {
        color: #71717a !important;
      }
      [data-ogsc] .footer-unsubscribe a {
        color: #71717a !important;
      }
      [data-ogsc] .section-heading {
        color: #93c5fd !important;
      }
      [data-ogsc] .section-subcopy {
        color: #a1a1aa !important;
      }
      [data-ogsc] .locator-card {
        background: #27272a !important;
        border-color: #52525b !important;
      }
      [data-ogsc] .locator-name {
        color: #93c5fd !important;
      }
      [data-ogsc] .locator-title {
        color: #a1a1aa !important;
      }
      [data-ogsc] .locator-company {
        color: #d4d4d8 !important;
      }
      [data-ogsc] .locator-link {
        color: #93c5fd !important;
      }
      [data-ogsc] .locator-insta-band-link,
      [data-ogsc] .locator-insta-band-link .locator-instagram-handle {
        color: #ffffff !important;
      }

    </style>
  </head>

  <body style="margin: 0; padding: 0; background: #f7f8fc;">
    <table
      width="100%"
      cellpadding="0"
      cellspacing="0"
      role="presentation"
      class="main-wrapper"
      style="padding: 24px 0;"
    >
      <tr>
        <td align="center">
          <!-- MAIN CARD WRAPPER -->
          <table
            width="100%"
            cellpadding="0"
            cellspacing="0"
            role="presentation"
            style="max-width: 600px; width: 100%; background: #ffffff; border-radius: 16px; box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1); overflow: hidden;"
            class="main-card"
          >
            <!-- BRAND BAND: solid blue, identical in light + dark mode.
                 Logo is always the white variant on blue — no per-mode
                 swap, no [data-ogsc]/[data-ogsb] tricks, no hidden
                 fallback img. Broken-image alt text falls back white on
                 blue so it still reads if S3 is unreachable.
                 Sized compact by default (~50px band on desktop) so the
                 header doesn't dominate the card's vertical hierarchy. -->
            <tr>
              <td
                align="center"
                class="brand-band"
                style="background: #005dff; padding: 12px 24px; line-height: 0;"
              >
                <img
                  src="${sanitizeUrl(COMPANY_LOGO_WHITE_URL)}"
                  alt="LocateNow"
                  width="96"
                  style="width: 96px; height: auto; display: block; margin: 0 auto; border: 0; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 18px; font-weight: 700;"
                />
              </td>
            </tr>

            <!-- HEADLINE + GREETING (below brand band) -->
            <tr>
              <td style="padding: 24px 24px 12px 24px;" class="inner-pad">
                <h1 style="font-size: 26px; margin: 0 0 8px 0; color: #005dff; font-weight: 700;" class="headline">
                  Your Apartment List
                </h1>
                <p class="greeting-text" style="margin: 0; font-size: 15px; color: #64748b; line-height: 1.5;">
                  ${escapeHtml(greeting)}
                </p>
              </td>
            </tr>

            ${propertiesHtml}

            <!-- PRICING DISCLOSURE -->
            <tr>
              <td style="padding: 4px 24px 20px 24px;" class="inner-pad">
                <p class="pricing-disclosure" style="margin: 0; font-size: 12px; color: #64748b; line-height: 1.45; border-left: 3px solid #ffd400; padding: 6px 0 6px 12px;">
                  Pricing and availability change frequently. Please confirm on each property's website before making any decision, or ask your locator to call on your behalf — that's what we do!
                </p>
              </td>
            </tr>

            <!-- LOCATOR SECTION -->
            <tr>
              <td style="padding: 16px 24px 5px 24px;" class="inner-pad">
                <h3 class="section-heading" style="font-size: 18px; margin: 0; color: #005dff; font-weight: 700;">
                  Ready to tour?
                  <span style="border-bottom: 3px solid #ffd400; padding-bottom: 2px;">Let's go!</span>
                </h3>
              </td>
            </tr>

            <tr>
              <td style="padding: 5px 24px 20px 24px;" class="inner-pad">
                <p class="section-subcopy" style="margin: 0; font-size: 12px; color: #64748b; line-height: 1.45;">
                  I can schedule tours, do virtual walkthroughs, or send videos of available units. Just reach out!
                </p>
              </td>
            </tr>

            <tr>
              <td style="padding: 0 24px 28px 24px;" class="inner-pad">
                <!-- Locator card — two stacked sections inside one rounded
                     wrapper. Overflow:hidden ensures the gradient band at
                     the bottom clips cleanly to the card's rounded corners
                     in clients that honor it. The border-radius on the
                     band td below is the non-overflow fallback. -->
                <table
                  width="100%"
                  cellpadding="0"
                  cellspacing="0"
                  class="locator-card"
                  style="background-color: #f5f5f7; border: 1px solid #e0e0e0; border-radius: 16px; overflow: hidden;"
                  role="presentation"
                >
                  <!-- TOP SECTION: avatar + name block + phone/email
                       stacked. Inner table width=auto so cells are tight
                       to content — explicit padding-left on the phone/
                       email cell sets the gap so the two blocks sit
                       side-by-side with clean, consistent spacing
                       regardless of card width. -->
                  <tr>
                    <td style="padding: 22px 24px;">
                      <table cellpadding="0" cellspacing="0" role="presentation">
                        <tr>
                          <!-- PHOTO -->
                          <td
                            width="80"
                            valign="middle"
                            class="locator-photo-cell"
                            style="padding-right: 16px;"
                          >
                            <img
                              src="${sanitizeUrl(locatorPhoto)}"
                              alt="${escapeHtml(locatorName)}"
                              style="width: 64px; height: 64px; border-radius: 50%; display: block; object-fit: cover; border: 3px solid #ffffff; box-shadow: 0 2px 8px rgba(0,0,0,0.12);"
                            />
                          </td>

                          <!-- NAME + TITLE + COMPANY (left side) -->
                          <td valign="middle" class="locator-info-cell">
                            <p class="locator-name" style="font-size: 18px; font-weight: 700; color: #005dff; margin: 0 0 2px 0;">
                              ${escapeHtml(locatorName)}
                            </p>
                            ${
                              locatorJobTitle
                                ? `<p class="locator-title" style="margin: 0 0 4px 0; font-size: 13px; color: #71717a;">${escapeHtml(locatorJobTitle)}</p>`
                                : ""
                            }
                            <p class="locator-company" style="margin: 0; font-size: 14px; font-weight: 600; color: #3f3f46;">
                              ${escapeHtml(COMPANY_NAME)}
                            </p>
                          </td>

                          <!-- PHONE + EMAIL stacked, left-aligned inside
                               their own cell with a fixed gap to the name
                               block via padding-left. -->
                          <td valign="middle" class="locator-contact-stack" style="padding-left: 36px; white-space: nowrap;">
                            <a class="locator-link" href="tel:${locatorPhone.replace(
                              /[^0-9]/g,
                              ""
                            )}" style="display: inline-block; color: #005dff; text-decoration: none; font-weight: 500; font-size: 14px; white-space: nowrap; margin-bottom: 4px;">
                              <span style="font-size: 14px;">📞</span> ${escapeHtml(locatorPhone)}
                            </a>
                            <br />
                            <a class="locator-link" href="${sanitizeUrl(`mailto:${locatorEmail}`)}" style="display: inline-block; color: #005dff; text-decoration: none; font-weight: 500; font-size: 14px; white-space: nowrap;">
                              <span style="font-size: 14px;">✉️</span> ${escapeHtml(locatorEmail)}
                            </a>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>

                  <!-- BOTTOM BAND: full-width Instagram gradient, echoes
                       the property card's identity band pattern. Whole
                       band is a single clickable anchor. Rounded bottom
                       corners on the td as a fallback for clients that
                       don't honor overflow:hidden on the parent table. -->
                  <tr>
                    <td class="locator-insta-band" style="background: linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%); padding: 0; border-radius: 0 0 15px 15px;">
                      <a
                        href="${sanitizeUrl(INSTAGRAM_URL)}"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="locator-insta-band-link"
                        style="display: block; padding: 10px 24px; color: #ffffff; text-decoration: none; text-align: center; font-size: 13px; font-weight: 600; letter-spacing: 0.2px;"
                      >
                        <img
                          src="${sanitizeUrl(INSTAGRAM_ICON_WHITE_URL)}"
                          alt=""
                          class="locator-insta-icon"
                          width="16"
                          height="16"
                          style="display: inline-block; width: 16px; height: 16px; vertical-align: middle; margin-right: 8px; margin-bottom: 2px; border: 0;"
                        />
                        <span style="color: #ffffff; vertical-align: middle;">Follow us</span>
                        <span style="padding: 0 8px; color: rgba(255,255,255,0.6); vertical-align: middle;">·</span>
                        <span class="locator-instagram-handle" style="color: #ffffff; font-weight: 700; vertical-align: middle;">${escapeHtml(INSTAGRAM_HANDLE)}</span>
                        <span style="margin-left: 6px; color: #ffffff; vertical-align: middle;">→</span>
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- FOOTER -->
            <!-- Postal address is CAN-SPAM §7704(a)(5) mandatory for any
                 commercial send. Brokerage address satisfies the "sender"
                 requirement since listings/updates promote Real McCoy's
                 services. Keep in sync with the brokerage's license-
                 registered address — a mismatch is an audit red flag. -->
            <tr>
              <td style="padding: 0 24px 6px 24px; text-align: center;" class="inner-pad">
                <p class="footer-credit" style="margin: 0; font-size: 11px; color: #a1a1aa;">
                  Affiliated with Real McCoy Realty Consultants
                </p>
              </td>
            </tr>
            <tr>
              <td style="padding: 0 24px 10px 24px; text-align: center;" class="inner-pad">
                <p class="footer-address" style="margin: 0; font-size: 11px; color: #a1a1aa;">
                  9615 Stoney Ridge Lane &middot; Johns Creek, GA 30002
                </p>
              </td>
            </tr>
            <!-- {{UNSUBSCRIBE_URL}} placeholder is rewritten server-side
                 in backend/api/routes/gmail.py via
                 unsubscribe_wire.substitute_unsubscribe_placeholder().
                 Resolution order:
                   - Personal mode  → /ep/{token} preferences center
                   - Campaign mode  → /u/{token} immediate unsubscribe
                   - mailto fallback when token mint is unavailable -->
            <tr>
              <td style="padding: 0 24px 24px 24px; text-align: center;" class="inner-pad">
                <p class="footer-unsubscribe" style="margin: 0; font-size: 11px; color: #a1a1aa; line-height: 1.5;">
                  ${escapeHtml(footerIntroCopy)} <a href="{{UNSUBSCRIBE_URL}}" style="color: #a1a1aa; text-decoration: underline;">${escapeHtml(footerActionLabel)}</a>.
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
};

// ==================== Gmail Status Check ====================

export async function checkGmailStatus(): Promise<GmailStatus> {
  try {
    const data = await fetchWithAuth<GmailStatus>("/gmail/status");
    return data;
  } catch (error) {
    console.error("Error checking Gmail status:", error);
    return {
      connected: false,
      email: null,
      connected_at: null,
      last_used_at: null,
    };
  }
}

// ==================== Email Service ====================

const LEGACY_EMAIL_CONFIG_KEY = "email_config";
const EMAIL_CONFIG_VERSION = 2;
const legacyScopedEmailConfigKey = (userId: string): string =>
  `persist:${userId}:email-config:v1`;

export const emailConfigStorageKey = (userId: string): string =>
  `persist:${userId}:email-config:v${EMAIL_CONFIG_VERSION}`;

type EditableEmailConfig = Partial<
  Pick<EmailConfig, "agentName" | "agentEmail" | "agentPhone" | "jobTitle">
>;

interface StoredEmailConfig {
  version: typeof EMAIL_CONFIG_VERSION;
  userId: string;
  timestamp: number;
  generation: number;
  keyGeneration: number;
  config: Pick<
    EmailConfig,
    "agentName" | "agentEmail" | "agentPhone" | "jobTitle"
  >;
}

interface LegacyStoredEmailConfig {
  version: 1;
  userId: string;
  timestamp: number;
  config: StoredEmailConfig["config"];
}

const parseLegacyStoredEmailConfig = (
  raw: string | null,
  expectedUserId: string,
  now = Date.now()
): LegacyStoredEmailConfig | null => {
  if (!raw || !valueFitsPolicy(raw, EMAIL_CONFIG_POLICY)) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<LegacyStoredEmailConfig>;
    const config = parsed.config as Partial<LegacyStoredEmailConfig["config"]>;
    if (
      parsed.version !== 1 ||
      parsed.userId !== expectedUserId ||
      !Number.isFinite(parsed.timestamp) ||
      (parsed.timestamp as number) > now + 5 * 60 * 1000 ||
      now - (parsed.timestamp as number) >= EMAIL_CONFIG_POLICY.ttlMs ||
      !config ||
      typeof config.agentName !== "string" ||
      typeof config.agentEmail !== "string" ||
      typeof config.agentPhone !== "string" ||
      typeof config.jobTitle !== "string"
    ) return null;
    return parsed as LegacyStoredEmailConfig;
  } catch {
    return null;
  }
};

const parseStoredEmailConfig = (
  raw: string | null,
  expectedUserId: string,
  expectedGeneration: number,
  expectedKeyGeneration: number,
  now = Date.now()
): StoredEmailConfig | null => {
  if (!raw || !valueFitsPolicy(raw, EMAIL_CONFIG_POLICY)) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredEmailConfig>;
    const config = parsed.config as Partial<StoredEmailConfig["config"]>;
    if (
      parsed.version !== EMAIL_CONFIG_VERSION ||
      parsed.userId !== expectedUserId ||
      parsed.generation !== expectedGeneration ||
      parsed.keyGeneration !== expectedKeyGeneration ||
      !Number.isFinite(parsed.timestamp) ||
      (parsed.timestamp as number) > now + 5 * 60 * 1000 ||
      now - (parsed.timestamp as number) >= EMAIL_CONFIG_POLICY.ttlMs ||
      !config ||
      typeof config.agentName !== "string" ||
      typeof config.agentEmail !== "string" ||
      typeof config.agentPhone !== "string" ||
      typeof config.jobTitle !== "string"
    ) {
      return null;
    }
    return parsed as StoredEmailConfig;
  } catch {
    return null;
  }
};

class EmailService {
  private identity: EmailConfigIdentity | null = null;
  private hasScopedConfig = false;

  // Agent/Locator config
  private agentName: string = "";
  private agentEmail: string = "";
  private agentPhone: string = "";
  private companyName: string = COMPANY_NAME;
  private jobTitle: string = "";
  private logoUrl: string = COMPANY_LOGO_URL;

  constructor() {
    // The legacy key has no owner. Attaching it to whichever account happens
    // to authenticate next would be a cross-user identity guess, so discard it.
    clientStore.removeItem(LEGACY_EMAIL_CONFIG_KEY);
  }

  private clearMemory() {
    this.agentName = "";
    this.agentEmail = "";
    this.agentPhone = "";
    this.jobTitle = "";
    this.companyName = COMPANY_NAME;
    this.logoUrl = COMPANY_LOGO_URL;
    this.hasScopedConfig = false;
  }

  private applyConfig(config: EditableEmailConfig) {
    if (config.agentName !== undefined) this.agentName = config.agentName;
    if (config.agentEmail !== undefined) this.agentEmail = config.agentEmail;
    if (config.agentPhone !== undefined) this.agentPhone = config.agentPhone;
    if (config.jobTitle !== undefined) this.jobTitle = config.jobTitle;
    this.companyName = COMPANY_NAME;
    this.logoUrl = COMPANY_LOGO_URL;
  }

  setUserId(userId: string): EmailConfigIdentity {
    this.clearMemory();
    clientStore.removeItem(LEGACY_EMAIL_CONFIG_KEY);
    const identity = {
      userId,
      generation: getPersistenceGeneration(userId),
    };
    this.identity = identity;

    const key = emailConfigStorageKey(userId);
    const raw = clientStore.getItem(key);
    const stored = parseStoredEmailConfig(
      raw,
      userId,
      identity.generation,
      clientStore.generation(key)
    );
    if (stored) {
      this.applyConfig(stored.config);
      this.hasScopedConfig = true;
    } else if (raw) {
      // Corrupt, stale-version, or owner-mismatched data is not a default.
      clientStore.removeItem(key);
    }
    if (!stored) {
      const legacyKey = legacyScopedEmailConfigKey(userId);
      const legacyRaw = clientStore.getItem(legacyKey);
      const invalidationMatchesGeneration = hasPersistenceInvalidationFence(
        userId,
        identity.generation
      );
      const legacy = invalidationMatchesGeneration
        ? null
        : parseLegacyStoredEmailConfig(legacyRaw, userId);
      let migratedDurably = false;
      if (legacy) {
        this.applyConfig(legacy.config);
        migratedDurably = this.setConfig(identity, legacy.config);
      }
      if (
        legacyRaw &&
        (invalidationMatchesGeneration || !legacy || migratedDurably)
      ) {
        clientStore.removeItem(legacyKey);
      }
    }
    return { ...identity };
  }

  resetUserConfig() {
    this.identity = null;
    this.clearMemory();
    clientStore.removeItem(LEGACY_EMAIL_CONFIG_KEY);
  }

  captureIdentity(userId: string): EmailConfigIdentity {
    return { userId, generation: getPersistenceGeneration(userId) };
  }

  isIdentityCurrent(identity: EmailConfigIdentity): boolean {
    return (
      this.identity?.userId === identity.userId &&
      this.identity.generation === identity.generation &&
      persistenceWriteAllowed(identity.userId, identity.generation)
    );
  }

  setConfig(
    identity: EmailConfigIdentity,
    config: EditableEmailConfig
  ): boolean {
    if (!this.isIdentityCurrent(identity)) return false;
    this.applyConfig(config);
    this.hasScopedConfig = true;
    const stored: StoredEmailConfig = {
      version: EMAIL_CONFIG_VERSION,
      userId: identity.userId,
      timestamp: Date.now(),
      generation: identity.generation,
      keyGeneration: clientStore.generation(
        emailConfigStorageKey(identity.userId)
      ),
      config: {
        agentName: this.agentName,
        agentEmail: this.agentEmail,
        agentPhone: this.agentPhone,
        jobTitle: this.jobTitle,
      },
    };
    const raw = JSON.stringify(stored);
    if (!valueFitsPolicy(raw, EMAIL_CONFIG_POLICY)) {
      clientStore.reportMemoryOnly(
        "oversize",
        emailConfigStorageKey(identity.userId)
      );
      return false;
    }
    return clientStore.setItemGuarded(
      emailConfigStorageKey(identity.userId),
      raw,
      stored.keyGeneration
    );
  }

  hydrateDefaults(
    identity: EmailConfigIdentity,
    config: EditableEmailConfig
  ): boolean {
    if (!this.isIdentityCurrent(identity)) return false;
    if (this.hasScopedConfig) return true;
    const clearEvent = readPersistenceEvent(
      clientStore.getItem(PERSISTENCE_LOCAL_CLEAR_KEY),
      "local-clear"
    );
    if (
      clearEvent?.userId === identity.userId &&
      clearEvent.generation === identity.generation
    ) {
      // Clear Local Data remains truthful across reload: server-derived
      // defaults are available in memory, but are not silently persisted
      // again until the user explicitly saves Email Settings.
      this.applyConfig(config);
      return true;
    }
    return this.setConfig(identity, config);
  }

  getConfig(): EmailConfig {
    if (!this.identity || !this.isIdentityCurrent(this.identity)) {
      return {
        agentName: "",
        agentEmail: "",
        agentPhone: "",
        companyName: COMPANY_NAME,
        jobTitle: "",
        logoUrl: COMPANY_LOGO_URL,
      };
    }
    return {
      agentName: this.agentName,
      agentEmail: this.agentEmail,
      agentPhone: this.agentPhone,
      companyName: this.companyName,
      jobTitle: this.jobTitle,
      logoUrl: this.logoUrl,
    };
  }

  // Send Guest Card via Gmail
  async sendGuestCard(data: GuestCardData): Promise<EmailResult> {
    try {
      // Generate HTML
      const htmlBody = generateGuestCardHtml(data);
      // sanitizeHeader strips CR/LF to prevent MIME header injection via
      // a client first/last name containing embedded newlines.
      const subject = sanitizeHeader(
        `Guest Card: ${data.client.first_name} ${data.client.last_name}`
      );

      // Send via Gmail
      const result = await fetchWithAuth<{
        success: boolean;
        message_id?: string;
        error?: string;
        tracking_id?: string;
      }>("/gmail/send", {
        method: "POST",
        body: JSON.stringify({
          to: data.recipientEmail,
          subject,
          html_body: htmlBody,
          from_name: sanitizeHeader(data.locatorName || this.agentName),
          source_type: "guest_card",
          client_id: data.client.id,
        }),
      });

      if (result.success) {
        // Activity logging moved to GuestCardDialog for more detailed tracking
      }

      return {
        success: result.success,
        messageId: result.message_id,
        error: result.error,
        provider: "gmail",
        trackingId: result.tracking_id,
      };
    } catch (error: any) {
      console.error("Guest card send error:", error);
      return {
        success: false,
        error: error.message || "Failed to send guest card",
      };
    }
  }

  // Send Listings via Gmail
  async sendListings(
    data: ListingEmailData,
    recipientEmail?: string,
    customSubject?: string,
    // Bimodal send architecture (Phase 2 Gmail-deliverability fix).
    //   "personal" (default · safer · UI label "Personal mode")
    //     → backend skips RFC 8058 List-Unsubscribe header + skips
    //       click/open tracking. CAN-SPAM footer link + postal address
    //       preserved. Designed for Gmail Primary tab placement.
    //   "campaign" (UI label "Ultra Marketing mode")
    //     → keeps full pre-Phase-2 behavior (header + tracking).
    sendMode: "personal" | "campaign" = "personal",
  ): Promise<EmailResult> {
    const toEmail = recipientEmail || data.client.email;

    if (!toEmail) {
      return { success: false, error: "No recipient email provided" };
    }

    try {
      // Generate HTML + matching plain-text alternative. Gmail's
      // classifier rewards matching pairs (HTML-only sends get the
      // Promotions-tab penalty). Plain-text is built from the same
      // ListingEmailData shape so subject/property data stays consistent.
      const footerLinkKind = sendMode === "personal" ? "preferences" : "unsubscribe";
      const htmlBody = generateListingEmailHtml({
        ...data,
        footerLinkKind,
      });
      const plainTextBody = generateListingEmailPlainText({
        ...data,
        footerLinkKind,
      });
      const propertyCount =
        data.groupedProperties?.length ||
        groupListingsByProperty(data.listings).length;

      // Use custom subject if provided, otherwise generate default.
      // sanitizeHeader strips CR/LF so a malformed customSubject or
      // first_name with embedded newlines can't inject extra headers.
      const subject = sanitizeHeader(
        customSubject?.trim() ||
          `Apartment Options for ${
            data.client.first_name || "You"
          } - ${propertyCount} ${
            propertyCount === 1 ? "Property" : "Properties"
          }`
      );

      // Send via Gmail
      const result = await fetchWithAuth<{
        success: boolean;
        message_id?: string;
        error?: string;
        tracking_id?: string;
        send_mode_used?: "personal" | "campaign";
        forced_to_campaign?: boolean;
      }>("/gmail/send", {
        method: "POST",
        body: JSON.stringify({
          to: toEmail,
          subject,
          html_body: htmlBody,
          plain_text_body: plainTextBody,
          from_name: sanitizeHeader(data.locatorName || this.agentName),
          source_type: "listing",
          client_id: data.client.id,
          send_mode: sendMode,
        }),
      });

      if (result.success && data.client.id) {
        // Log activity
        await this.logActivity("listing_shared", data.client.id, {
          client_name: `${data.client.first_name} ${data.client.last_name}`,
          recipient_email: toEmail,
          property_count: propertyCount,
          unit_count: data.listings.length,
          provider: "gmail",
        });
      }

      return {
        success: result.success,
        messageId: result.message_id,
        error: result.error,
        provider: "gmail",
        trackingId: result.tracking_id,
      };
    } catch (error: any) {
      console.error("Listings send error:", error);
      return {
        success: false,
        error: error.message || "Failed to send listings",
      };
    }
  }

  private async logActivity(
    activityType: string,
    clientId: string,
    metadata: Record<string, any>
  ) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from("activities").insert({
        user_id: user.id,
        activity_type: activityType,
        entity_type: "client",
        entity_id: clientId,
        metadata,
      });
    } catch (error) {
      console.error("Error logging activity:", error);
    }
  }

  // Preview HTML without sending (for local preview only)
  previewGuestCard(data: GuestCardData): string {
    return generateGuestCardHtml(data);
  }

  previewListingEmail(data: ListingEmailData): string {
    return generateListingEmailHtml(data);
  }
}

// ==================== Update Property Specials ====================

export async function updatePropertySpecials(
  propertyId: string,
  specials: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await fetchWithAuth(`/search/properties/${propertyId}`, {
      method: "PATCH",
      body: JSON.stringify({ specials }),
    });
    return { success: true };
  } catch (error: any) {
    console.error("Error updating property specials:", error);
    return {
      success: false,
      error: error.message || "Failed to update specials",
    };
  }
}

// ==================== Fetch Locator Profile ====================

export interface LocatorProfile {
  id: string;
  full_name?: string;
  avatar_url?: string;
  phone?: string;
  email?: string;
  job_title?: string;
}

export async function fetchLocatorProfile(): Promise<LocatorProfile | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: profile, error } = await supabase
      .from("profiles")
      .select("id, full_name, avatar_url, phone, email, job_title")
      .eq("id", user.id)
      .single();

    if (error) {
      console.error("Error fetching profile:", error);
      return null;
    }

    return profile;
  } catch (error) {
    console.error("Error fetching locator profile:", error);
    return null;
  }
}

export const emailService = new EmailService();
export default emailService;

// ==================== File / Attachment Helpers ====================

/**
 * Convert a File to a base64-encoded string (without the data: URL prefix).
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip "data:<mime>;base64," prefix
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export interface LeaseEmailParams {
  to: string;
  subject: string;
  html_body: string;
  from_name?: string;
  file: File;
  source_type?: string;
  client_id?: string;
}

export interface LeaseEmailResult {
  success: boolean;
  message_id?: string;
  error?: string;
  tracking_id?: string;
}

/**
 * Send an email via the user's connected Gmail account with a single file
 * attachment. Used by the Upload Lease workflow.
 */
export async function sendLeaseEmail(
  params: LeaseEmailParams
): Promise<LeaseEmailResult> {
  const base64 = await fileToBase64(params.file);
  return fetchWithAuth<LeaseEmailResult>("/gmail/send", {
    method: "POST",
    body: JSON.stringify({
      to: params.to,
      subject: params.subject,
      html_body: params.html_body,
      from_name: params.from_name,
      attachments: [
        {
          filename: params.file.name,
          content: base64,
          mime_type: params.file.type || "application/pdf",
        },
      ],
      source_type: params.source_type || "lease",
      client_id: params.client_id,
    }),
  });
}
